import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SidebarProvider, useSidebar } from "./context/SidebarContext";
import { PermissionProvider, usePermissions } from "./context/PermissionContext";
import { Toaster } from "./components/ui/sonner";

// Components
import Sidebar from "./components/Sidebar";
import EditAccessAutoPopup from "./components/EditAccessAutoPopup";
import Header from "./components/Header";
import AccessDenied from "./components/AccessDenied";

// Pages
import Login from "./pages/Login";
import InsightsHub from "./pages/InsightsHub";
import LeadAnalytics from "./pages/LeadAnalytics";
import Leads from "./pages/Leads";
import FinalQC from "./pages/FinalQC";
import LeadDetails from "./pages/LeadDetails";
import Tasks from "./pages/Tasks";
import Workflows from "./pages/Workflows";
import WorkflowBuilder from "./pages/WorkflowBuilder";
import ReportsWrapper from "./pages/ReportsWrapper";
import CallHistory from "./pages/CallHistory";
import CallAnalytics from "./pages/CallAnalytics";
import CallToppers from "./pages/CallToppers";
import Properties from "./pages/Properties";
import PropertyDetails from "./pages/PropertyDetails";
import AddProperty from "./pages/AddProperty";
import Campaigns from "./pages/Campaigns";
import Chatbot from "./pages/Chatbot";
import ChatbotHub from "./pages/chatbot/ChatbotHub";
import WhatsAppAnalytics from "./pages/WhatsAppAnalytics";
import LegalPolicyPage from "./pages/LegalPolicyPage";

// WhatsApp Module Pages
import WhatsAppBroadcast from "./pages/whatsapp/WhatsAppBroadcast";
import WhatsAppTemplates from "./pages/whatsapp/WhatsAppTemplates";
import WhatsAppScheduled from "./pages/whatsapp/WhatsAppScheduled";
import WhatsAppLogs from "./pages/whatsapp/WhatsAppLogs";
import WhatsAppInbox from "./pages/whatsapp/WhatsAppInbox";
import WhatsAppLabels from "./pages/whatsapp/WhatsAppLabels";
import WhatsAppChatbot from "./pages/whatsapp/WhatsAppChatbot";
import AutomationTemplates from "./pages/whatsapp/AutomationTemplates";

// Hotel Lifecycle Module
import { LifecycleDashboard, HotelLifecycleDetail, OffboardingWorkflow } from "./modules/hotel-lifecycle";

// Enterprise Settings System
import SettingsLayout from "./pages/settings/SettingsLayout";
import UserManagement from "./pages/settings/UserManagement";
import SidebarSettings from "./pages/settings/SidebarSettings";
import LabelsSettings from "./pages/settings/LabelsSettings";
import ProfileSettings from "./pages/settings/ProfileSettings";
import PasswordSettings from "./pages/settings/PasswordSettings";
import ModuleManagement from "./pages/settings/ModuleManagement";
import CustomFieldsManagement from "./pages/settings/CustomFieldsManagement";
import LegalComplianceSettings from "./pages/settings/LegalComplianceSettings";
import {
  DashboardSettings,
  HotelOnboardedSettings,
  PropertiesSettings,
  HotelLifecycleSettings,
  BroadcastSettings,
  ChatbotSettings,
  WhatsAppSettings,
  AnalyticsSettings,
  AuditHistorySettings,
  GeneralSettings,
} from "./pages/settings/ModuleSettings";
import AuditHistory from "./pages/AuditHistory";
import AdminDashboard from "./pages/AdminDashboard";
import APIKeySettings from "./pages/settings/APIKeySettings";
import DataManagement from "./pages/settings/DataManagement";
import ChecklistMonitorSettings from "./pages/settings/ChecklistMonitorSettings";
import OTATimerSettings from "./pages/settings/OTATimerSettings";
import CommercialApprovalSettings from "./pages/settings/CommercialApprovalSettings";
import PropertyMappingSettings from "./pages/settings/PropertyMappingSettings";
import GmailIntegrationSettings from "./pages/settings/GmailIntegrationSettings";
import MetaLeadAdsSettings from "./pages/settings/MetaLeadAdsSettings";
import SuperhostAdminSettings from "./pages/settings/SuperhostAdminSettings";
import EmailLogs from "./pages/settings/EmailLogs";
import QCSettings from "./pages/settings/QCSettings";
import OnboardingFormSettings from "./pages/settings/OnboardingFormSettings";
import SectionAccessSettings from "./pages/settings/SectionAccessSettings";
import ReportsConfigSettings from "./pages/settings/ReportsConfigSettings";
import WAGroupBroadcast from "./pages/WAGroupBroadcast";
import ErrorBoundary from "./components/ErrorBoundary";

// Protected Route component - redirects to login if not authenticated
const ProtectedRoute = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
};

/**
 * PermissionRoute — guards a route by permission module key.
 * @param {string} moduleKey - The permission module to check (e.g. 'leads', 'insights')
 * @param {boolean} adminOnly - If true, only admins can access
 * @param {React.ReactNode} children - The page element to render
 */
const PermissionRoute = ({ moduleKey, adminOnly, children }) => {
  const { canView, isAdmin, loading: permLoading } = usePermissions();
  const { loading: authLoading } = useAuth();

  if (authLoading || permLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  // Admin-only routes
  if (adminOnly && !isAdmin) {
    return <AccessDenied />;
  }

  // Permission-based routes (admin always passes)
  if (moduleKey && !isAdmin && !canView(moduleKey)) {
    return <AccessDenied />;
  }

  return children;
};

// Layout component with sidebar and header
const AppLayout = ({ children, title }) => {
  const { isExpanded } = useSidebar();
  const contentMargin = isExpanded ? 'ml-60' : 'ml-[70px]';
  
  return (
    <div className="app-layout flex min-h-screen bg-gray-100 overflow-x-hidden">
      <Sidebar />
      <div className={`main-content flex-1 ${contentMargin} transition-all duration-300 ease-in-out min-w-0`}>
        <Header title={title} />
        <main className="p-6 overflow-x-hidden">{children}</main>
      </div>
      {/* iter246 — Global auto-popup for pending GTM Sales edit-access requests. */}
      <EditAccessAutoPopup />
    </div>
  );
};

// Public Layout
const PublicLayout = ({ children }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col">
      <main className="flex-1 w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
      <footer className="bg-white/50 border-t border-gray-200 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <p className="text-center text-sm text-gray-400">
            © {new Date().getFullYear()} All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

// Page wrapper helpers
const P = ({ element, title, moduleKey, adminOnly }) => (
  <AppLayout title={title}>
    <PermissionRoute moduleKey={moduleKey} adminOnly={adminOnly}>
      {element}
    </PermissionRoute>
  </AppLayout>
);

const PublicPageWrapper = ({ element }) => (
  <PublicLayout>{element}</PublicLayout>
);

function AppRoutes() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
    <Routes>
      {/* PUBLIC ROUTES */}
      <Route path="/login" element={isAuthenticated ? <Navigate to="/insights" replace /> : <Login />} />
      <Route path="/properties/new" element={<PublicPageWrapper element={<AddProperty />} />} />
      <Route path="/legal" element={<LegalPolicyPage />} />
      <Route path="/privacy-policy" element={<LegalPolicyPage />} />
      
      {/* PROTECTED ROUTES — All gated by PermissionRoute */}
      <Route element={<ProtectedRoute />}>
        <Route path="/" element={<Navigate to="/insights" replace />} />

        {/* CRM */}
        <Route path="/hotel-onboarded" element={<P element={<Leads />} title="Hotel Onboarded" moduleKey="leads" />} />
        <Route path="/insights" element={<P element={<InsightsHub />} title="Insights" moduleKey="insights" />} />
        <Route path="/dashboard" element={<Navigate to="/insights" replace />} />
        <Route path="/comparison" element={<Navigate to="/insights" replace />} />
        <Route path="/ota-management" element={<Navigate to="/insights" replace />} />
        <Route path="/performance-insights" element={<Navigate to="/insights" replace />} />
        <Route path="/leads" element={<P element={<Leads />} title="Leads" moduleKey="leads" />} />
        <Route path="/final-qc" element={<P element={<FinalQC />} title="Final QC" moduleKey="leads" />} />
        <Route path="/lead-analytics" element={<P element={<LeadAnalytics />} title="Hotel Onboarded Analytics" moduleKey="leadAnalytics" />} />
        <Route path="/leads/:id" element={<P element={<LeadDetails />} title="Lead Details" moduleKey="leads" />} />
        <Route path="/tasks" element={<P element={<Tasks />} title="Tasks" moduleKey="tasks" />} />
        <Route path="/workflows" element={<P element={<Workflows />} title="Workflows" moduleKey="workflows" />} />
        <Route path="/workflow/new" element={<P element={<WorkflowBuilder />} title="Create Workflow" moduleKey="workflows" />} />
        <Route path="/workflow/:id" element={<P element={<WorkflowBuilder />} title="Edit Workflow" moduleKey="workflows" />} />
        <Route path="/reports" element={<P element={<ReportsWrapper />} title="Reports" moduleKey="reports" />} />

        {/* Calls */}
        <Route path="/call-history" element={<P element={<CallHistory />} title="Call History" moduleKey="callHistory" />} />
        <Route path="/call-analytics" element={<P element={<CallAnalytics />} title="Call Analytics" moduleKey="callAnalytics" />} />
        <Route path="/call-toppers" element={<P element={<CallToppers />} title="Call Toppers" moduleKey="callToppers" />} />

        {/* Properties */}
        <Route path="/properties" element={<P element={<Properties />} title="Properties" moduleKey="properties" />} />
        <Route path="/properties/:id" element={<P element={<PropertyDetails />} title="Property Details" moduleKey="properties" />} />
        <Route path="/lifecycle" element={<P element={<LifecycleDashboard />} title="Hotel Lifecycle" moduleKey="hotelLifecycle" />} />
        <Route path="/lifecycle/:id" element={<P element={<HotelLifecycleDetail />} title="Hotel Lifecycle" moduleKey="hotelLifecycle" />} />
        <Route path="/hotel-lifecycle/offboarding" element={<P element={<OffboardingWorkflow />} title="Offboarding Workflow" moduleKey="hotelLifecycle" />} />
        <Route path="/hotel-lifecycle/offboarding/:id" element={<P element={<OffboardingWorkflow />} title="Offboarding Workflow" moduleKey="hotelLifecycle" /> } />

        {/* WhatsApp */}
        <Route path="/inbox" element={<P element={<WhatsAppInbox />} title="WhatsApp Inbox" moduleKey="inbox" />} />
        <Route path="/wa-group-broadcast" element={<P element={<WAGroupBroadcast />} title="WhatsApp Group Broadcast" moduleKey="wa_group_broadcast" />} />
        <Route path="/campaigns" element={<P element={<Campaigns />} title="Campaigns" moduleKey="campaigns" />} />
        <Route path="/automation" element={<P element={<WhatsAppBroadcast />} title="WhatsApp Broadcast" moduleKey="broadcast" />} />
        <Route path="/automation/new" element={<P element={<WhatsAppBroadcast />} title="New Broadcast" moduleKey="broadcast" />} />
        <Route path="/automation/templates" element={<Navigate to="/chatbot?tab=templates" replace />} />
        <Route path="/automation/scheduled" element={<P element={<WhatsAppScheduled />} title="Scheduled Messages" moduleKey="broadcast" />} />
        <Route path="/automation/logs" element={<P element={<WhatsAppLogs />} title="Broadcast Logs" moduleKey="broadcast" />} />
        <Route path="/automation/labels" element={<P element={<WhatsAppLabels />} title="Labels" moduleKey="broadcast" />} />
        <Route path="/automation/chatbot" element={<Navigate to="/chatbot?tab=rules" replace />} />
        <Route path="/automation/auto-templates" element={<Navigate to="/chatbot?tab=templates" replace />} />
        <Route path="/chatbot" element={<P element={<ChatbotHub />} title="Chatbot Center" moduleKey="chatbot" />} />
        <Route path="/chatbot-legacy" element={<P element={<Chatbot />} title="Chatbot (Legacy)" moduleKey="chatbot" />} />
        <Route path="/whatsapp-analytics" element={<P element={<WhatsAppAnalytics />} title="WhatsApp Analytics" moduleKey="whatsappAnalytics" />} />
        
        {/* Settings — accessible to all, SettingsLayout guards admin sections internally */}
        <Route path="/settings" element={<P element={<SettingsLayout />} title="Settings" />}>
          <Route index element={<Navigate to="/settings/profile" replace />} />
          <Route path="profile" element={<ProfileSettings />} />
          <Route path="password" element={<PasswordSettings />} />
          <Route path="users" element={<UserManagement />} />
          <Route path="sidebar" element={<SidebarSettings />} />
          <Route path="labels" element={<LabelsSettings />} />
          <Route path="modules" element={<ModuleManagement />} />
          <Route path="custom-fields" element={<CustomFieldsManagement />} />
          <Route path="dashboard" element={<DashboardSettings />} />
          <Route path="hotel-onboarded" element={<HotelOnboardedSettings />} />
          <Route path="properties" element={<PropertiesSettings />} />
          <Route path="hotel-lifecycle" element={<HotelLifecycleSettings />} />
          <Route path="broadcast" element={<BroadcastSettings />} />
          <Route path="chatbot" element={<ChatbotSettings />} />
          <Route path="whatsapp" element={<WhatsAppSettings />} />
          <Route path="analytics" element={<AnalyticsSettings />} />
          <Route path="audit-history" element={<AuditHistorySettings />} />
          <Route path="general" element={<GeneralSettings />} />
          <Route path="legal" element={<LegalComplianceSettings />} />
          <Route path="api-keys" element={<APIKeySettings />} />
          <Route path="data-management" element={<DataManagement />} />
          <Route path="checklist-monitor" element={<ChecklistMonitorSettings />} />
          <Route path="ota-timers" element={<OTATimerSettings />} />
          <Route path="approval-timeout" element={<CommercialApprovalSettings />} />
          <Route path="mapping" element={<PropertyMappingSettings />} />
          <Route path="qc-settings" element={<QCSettings />} />
          <Route path="onboarding-form-settings" element={<OnboardingFormSettings />} />
          <Route path="section-access" element={<SectionAccessSettings />} />
          <Route path="reports-config" element={<ReportsConfigSettings />} />
          <Route path="gmail" element={<GmailIntegrationSettings />} />
          <Route path="integrations/meta" element={<MetaLeadAdsSettings />} />
          <Route path="superhost-admin" element={<SuperhostAdminSettings />} />
          <Route path="email-logs" element={<EmailLogs />} />
        </Route>
        
        {/* Admin-only: Audit History */}
        <Route path="/audit-history" element={<P element={<AuditHistory />} title="Audit History" adminOnly />} />
        <Route path="/admin-dashboard" element={<P element={<AdminDashboard />} title="Admin Dashboard" adminOnly />} />
      </Route>

      {/* Catch-all */}
      <Route path="*" element={isAuthenticated ? <Navigate to="/insights" replace /> : <Navigate to="/login" replace />} />
    </Routes>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <PermissionProvider>
            <SidebarProvider>
              <AppRoutes />
            </SidebarProvider>
          </PermissionProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
